// @file vuecomposable for command bar functionality
import type { CommandBarStorageKey } from '@@/bits/command_bar'
import { trackEventOnlyOnce } from '@@/bits/command_bar'
import { isAppUsing } from '@@/bits/flip'
import { safeLocalStorage } from '@@/bits/safe_storage'
import type { WallViz } from '@@/types'
import { until } from '@vueuse/core'
import { computed } from 'vue'

/**
 * Waits for the CommandBar API to be available on the global window object.
 * Uses polling to check every 100ms until window.CommandBar is defined.
 *
 * @returns Promise<void> - Resolves when CommandBar API is available
 */
const waitForCommandBar = async (): Promise<void> => {
  return await new Promise<void>((resolve) => {
    const checkCommandBar = (): void => {
      if (window.CommandBar != null) {
        resolve()
      } else {
        setTimeout(checkCommandBar, 100) // Check every 100ms
      }
    }
    checkCommandBar()
  })
}

/**
 * Waits for the debug toolbar element to be present in the DOM.
 * Uses polling to check every 100ms for element with data-testid="debug-toolbar".
 * This is because it's possible that our events are sent before the debug toolbar is ready.
 * This is a workaround to avoid the issue.
 * Currently, this is only used in development mode.
 *
 * @param maxWaitTime - Maximum time to wait in milliseconds (default: 5000ms)
 * @param maxRetries - Maximum number of retries (default: 50)
 * @returns Promise<void> - Resolves when debug toolbar element is found, rejects on timeout
 */
const waitForDebugToolbar = async (maxWaitTime: number = 5000, maxRetries: number = 50): Promise<void> => {
  return await new Promise<void>((resolve, reject) => {
    const startTime = Date.now()
    let retryCount = 0
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const checkElement = (): void => {
      const element = document.querySelector('[data-testid="debug-toolbar"]')

      if (element) {
        if (timeoutId) clearTimeout(timeoutId)
        resolve()
        return
      }

      const elapsedTime = Date.now() - startTime
      retryCount++

      if (elapsedTime >= maxWaitTime || retryCount >= maxRetries) {
        if (timeoutId) clearTimeout(timeoutId)
        reject(new Error(`Debug toolbar not found after ${retryCount} retries (${elapsedTime}ms)`))
        return
      }

      timeoutId = setTimeout(checkElement, 100) // Check every 100ms
    }

    checkElement()
  })
}

/**
 * Vue composable for Command Bar functionality.
 *
 * IMPORTANT: This function imports Pinia stores asynchronously to:
 * - Reduce initial bundle size by code-splitting store dependencies
 * - Prevent unnecessary store initialization when CommandBar is disabled
 * - Improve initial page load performance through lazy loading
 * - Follow consistent patterns with other CommandBar async components
 *
 * @returns Promise<void> - Resolves when all watchers are set up
 */
export const useCommandBarEvents = async (): Promise<void> => {
  // Only proceed if CommandBar is enabled
  if (!isAppUsing('commandBar')) {
    return
  }

  // Async import stores
  const { useSurfacePermissionsStore } = await import('@@/pinia/surface_permissions')
  const { useSurfaceStore } = await import('@@/pinia/surface')
  const { useSurfacePostPropertiesStore } = await import('@@/pinia/surface_post_properties')
  const { useSurfacePostsStore } = await import('@@/pinia/surface_posts')
  const { useSurfaceSectionsStore } = await import('@@/pinia/surface_sections')

  // Wait for CommandBar to be ready
  await waitForCommandBar()

  // In development, wait for debug toolbar to be ready
  if (process.env.NODE_ENV === 'development') {
    try {
      await waitForDebugToolbar()
    } catch (error) {
      console.warn(
        'Debug toolbar not found. Did you install the chrome extension for CommandAI? Continuing without waiting:',
        error,
      )
    }
  }

  // Note: to setup nudge correctly with the tour, go to app.commandbar.com
  // open the tour, switch to the Targeting tab, in the 'When' section, enter the nudge key (w/o the prefix and suffix) under Event name field

  // #region table format release nudge
  const tableFormatNudgeKey: CommandBarStorageKey = 'commandbar_surfaceTableFormatNudge_triggered'
  if (!safeLocalStorage.getItem(tableFormatNudgeKey)) {
    const shouldShowTableFormatNudge = computed(() => {
      const wallCustomProperties = useSurfacePostPropertiesStore().wallCustomPostPropertiesById
      const isWallAdmin = useSurfacePermissionsStore().canIAdminister
      const wallViz = useSurfaceStore().viz

      const hasCustomProperties = Object.keys(wallCustomProperties).length > 0
      const wallVizForNotShowingNudge: WallViz[] = ['whiteboard', 'table']
      const canBeConvertedToTable = isWallAdmin && !wallVizForNotShowingNudge.includes(wallViz)

      return hasCustomProperties && canBeConvertedToTable
    })

    void until(shouldShowTableFormatNudge)
      .toBeTruthy()
      .then(() => {
        trackEventOnlyOnce('surfaceTableFormatNudge', { setLocalStorage: false })
      })
  }
  // #endregion

  // #region surface slideshow nudge
  const slideshowNudgeKey: CommandBarStorageKey = 'commandbar_surfaceSlideshowNudge_triggered'
  if (!safeLocalStorage.getItem(slideshowNudgeKey)) {
    const shouldShowSlideshowNudge = computed(() => {
      const numOfSections = useSurfaceSectionsStore().numOfSections
      const currentPostsCount = useSurfacePostsStore().currentPostsCount

      return numOfSections >= 2 && currentPostsCount >= 10
    })

    void until(shouldShowSlideshowNudge)
      .toBeTruthy()
      .then(() => {
        trackEventOnlyOnce('surfaceSlideshowNudge', { setLocalStorage: true })
      })
  }
  // #endregion
}
